import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FiyuuSendDraft, courierModuleService, formatMoney, toApiSource } from './courierModuleService';

interface Props {
  open: boolean;
  branchId: string;
  order: any | null;
  partNo?: number;
  partCount?: number;
  onClose: () => void;
  /** Fiyuu siparişi kabul etti; numara ile atama yapılır. */
  onSent: (externalOrderId: string) => void;
}

const PAYMENT_TYPES = [
  'cash', 'offline-credit-card', 'offline-multinet', 'offline-sodexo', 'offline-ticket', 'offline-metropol-card', 'offline-setcard',
  'online-credit-card', 'online-setcard', 'online-ticket', 'online-metropol', 'online-multinet', 'online-pluxee',
];

type Field = 'firstName' | 'lastName' | 'phone' | 'city' | 'county' | 'neighborhood' | 'street' | 'buildingNumber' | 'doorNumber';
const REQUIRED: Field[] = ['firstName', 'lastName', 'phone', 'city', 'county', 'neighborhood', 'street', 'buildingNumber', 'doorNumber'];

const phoneOk = (v: string) => {
  let d = v.replace(/\D/g, '');
  if (d.length === 12 && d.startsWith('90')) d = d.slice(2);
  if (d.length === 11 && d.startsWith('0')) d = d.slice(1);
  return d.length === 10 && d[0] !== '0';
};

/**
 * Fiyuu'ya gönder: siparişten doldurulan müşteri, adres ve ödeme bilgisi kontrol edilir, eksikler girilir.
 * Fiyuu reddederse mesajı gösterilir ve hiçbir şey değişmez; kabul ederse sipariş Fiyuu kuryesine atanır.
 */
const FiyuuSendDialog: React.FC<Props> = ({ open, branchId, order, partNo = 1, partCount = 1, onClose, onSent }) => {
  const { t } = useTranslation('salesSystem');
  const [draft, setDraft] = useState<FiyuuSendDraft | null>(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!open || !order) return;
    setDraft(null);
    setError(null);
    setTouched(false);
    setLoading(true);
    courierModuleService.fiyuuDraft(branchId, toApiSource(order.source), Number(order.id))
      .then(setDraft)
      .catch((e: any) => setError(e?.message || t('courierModule.fiyuu.loadFailed')))
      .finally(() => setLoading(false));
  }, [open, order, branchId, t]);

  const set = (patch: Partial<FiyuuSendDraft>) => setDraft((d) => (d ? { ...d, ...patch } : d));

  const missing = useMemo(() => {
    if (!draft) return [] as Field[];
    return REQUIRED.filter((f) => {
      const v = String((draft as any)[f] ?? '').trim();
      if (f === 'phone') return !phoneOk(v);
      if (f === 'firstName') return v.length < 2;
      return v.length === 0;
    });
  }, [draft]);

  const field = (f: Field, extra?: Partial<React.ComponentProps<typeof TextField>>) => (
    <TextField size="small" fullWidth label={t(`courierModule.fiyuu.fields.${f}`)} value={(draft as any)?.[f] ?? ''}
      onChange={(e) => set({ [f]: e.target.value } as any)}
      error={touched && missing.includes(f)} required {...extra} />
  );

  const send = async () => {
    setTouched(true);
    if (!draft || missing.length > 0) return;
    setSending(true);
    setError(null);
    try {
      const res = await courierModuleService.fiyuuSend({
        branchId,
        orderSource: draft.orderSource,
        orderRef: draft.orderRef,
        partNo,
        partCount,
        firstName: draft.firstName,
        lastName: draft.lastName,
        phone: draft.phone,
        extensionPhone: draft.extensionPhone || null,
        city: draft.city,
        county: draft.county,
        neighborhood: draft.neighborhood,
        street: draft.street,
        buildingName: draft.buildingName || null,
        buildingNumber: draft.buildingNumber,
        floor: draft.floor || null,
        doorNumber: draft.doorNumber,
        fullAddress: draft.fullAddress || null,
        addressDirection: draft.addressDirection || null,
        latitude: draft.latitude ?? null,
        longitude: draft.longitude ?? null,
        paymentType: draft.paymentType,
        totalAmount: draft.totalAmount,
        note: draft.note || null,
      });
      onSent(res.externalOrderId);
    } catch (e: any) {
      setError(e?.message || t('courierModule.fiyuu.sendFailed'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !sending && onClose()} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="h6" component="span">{t('courierModule.fiyuu.title')}</Typography>
        {order && <Chip size="small" label={order.orderNumber || `#${order.id}`} />}
        {draft && <Chip size="small" color={draft.environment === 'live' ? 'success' : 'default'}
          label={draft.environment === 'live' ? t('courierModule.fiyuu.live') : t('courierModule.fiyuu.test')} />}
      </DialogTitle>
      <DialogContent dividers>
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>}
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        {draft && !draft.connectionReady && <Alert severity="warning" sx={{ mb: 1.5 }}>{draft.connectionProblem}</Alert>}
        {draft?.previousExternalOrderId && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>{t('courierModule.fiyuu.alreadySent', { id: draft.previousExternalOrderId })}</Alert>
        )}
        {draft && (
          <Stack spacing={2}>
            <Typography variant="overline" color="text.secondary">{t('courierModule.fiyuu.customer')}</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 1.5 }}>
              {field('firstName')}
              {field('lastName')}
              {field('phone', { helperText: t('courierModule.fiyuu.phoneHint') })}
            </Box>
            <TextField size="small" label={t('courierModule.fiyuu.fields.extensionPhone')} value={draft.extensionPhone ?? ''}
              onChange={(e) => set({ extensionPhone: e.target.value })} helperText={t('courierModule.fiyuu.extensionHint')} sx={{ maxWidth: 320 }} />

            <Typography variant="overline" color="text.secondary">{t('courierModule.fiyuu.address')}</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 1.5 }}>
              {field('city')}
              {field('county')}
              {field('neighborhood')}
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr 1fr 1fr' }, gap: 1.5 }}>
              {field('street')}
              {field('buildingNumber')}
              <TextField size="small" label={t('courierModule.fiyuu.fields.floor')} value={draft.floor ?? ''} onChange={(e) => set({ floor: e.target.value })} />
              {field('doorNumber')}
            </Box>
            <TextField size="small" label={t('courierModule.fiyuu.fields.buildingName')} value={draft.buildingName ?? ''}
              onChange={(e) => set({ buildingName: e.target.value })} />
            <TextField size="small" multiline minRows={2} label={t('courierModule.fiyuu.fields.fullAddress')} value={draft.fullAddress}
              onChange={(e) => set({ fullAddress: e.target.value })} helperText={t('courierModule.fiyuu.fullAddressHint')} />
            <TextField size="small" label={t('courierModule.fiyuu.fields.addressDirection')} value={draft.addressDirection ?? ''}
              onChange={(e) => set({ addressDirection: e.target.value })} />
            <Typography variant="caption" color="text.secondary">
              {draft.latitude != null && draft.longitude != null
                ? t('courierModule.fiyuu.location', { lat: draft.latitude.toFixed(5), lng: draft.longitude.toFixed(5) })
                : t('courierModule.fiyuu.noLocation')}
            </Typography>

            <Typography variant="overline" color="text.secondary">{t('courierModule.fiyuu.payment')}</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
              <TextField select size="small" label={t('courierModule.fiyuu.fields.paymentType')} value={draft.paymentType}
                onChange={(e) => set({ paymentType: e.target.value })} sx={{ minWidth: 260 }}>
                {PAYMENT_TYPES.map((p) => <MenuItem key={p} value={p}>{t(`courierModule.fiyuu.paymentTypes.${p}`, { defaultValue: p })}</MenuItem>)}
              </TextField>
              <Typography variant="body1" fontWeight={700}>{formatMoney(draft.totalAmount)}</Typography>
              {draft.isPaid && <Chip size="small" color="success" label={t('courierModule.fiyuu.paid')} />}
            </Stack>
            {draft.paymentType.startsWith('online') && <Alert severity="info">{t('courierModule.fiyuu.onlineHint')}</Alert>}
            <TextField size="small" label={t('courierModule.fiyuu.fields.note')} value={draft.note ?? ''} onChange={(e) => set({ note: e.target.value })} />

            {touched && missing.length > 0 && (
              <Alert severity="error">{t('courierModule.fiyuu.missing', { fields: missing.map((f) => t(`courierModule.fiyuu.fields.${f}`)).join(', ') })}</Alert>
            )}
            <Typography variant="caption" color="text.secondary">{t('courierModule.fiyuu.hint')}</Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button disabled={sending} onClick={onClose}>{t('courierModule.fiyuu.cancel')}</Button>
        <Button variant="contained" disabled={sending || !draft?.connectionReady} onClick={send}>
          {sending ? <CircularProgress size={18} color="inherit" /> : t('courierModule.fiyuu.send')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FiyuuSendDialog;
